import {
  deleteFollowUpAttachment,
  listFollowUpAttachments,
  uploadFollowUpAttachment,
} from '@/services/cardService';
import { cn } from '@/utils/cn';
import { ErrorMessage } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiFile, FiTrash2, FiUpload } from 'react-icons/fi';
import { InfoBubble } from './InfoBubble';

interface FileAttachment {
  name: string;
  url: string;
  size: number;
}

interface FileInputProps {
  name: string;
  cardId: number;
  label?: string;
  isRequired?: boolean;
  helper?: string;
  error?: string | boolean;
  useFormik?: boolean;
  containerStyle?: string;
  labelStyle?: string;
  inputStyle?: string;
  disabled?: boolean;
  maxSizeMB?: number;
  allowedTypes?: string[];
}

export const FileInput = ({
  name,
  cardId,
  label,
  isRequired = false,
  helper,
  error,
  useFormik = true,
  containerStyle = '',
  labelStyle = '',
  inputStyle = '',
  disabled = false,
  maxSizeMB = 25,
  allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'image/jpeg',
    'image/jpg',
  ],
}: FileInputProps) => {
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const { t } = useTranslation('fileInput');

  // Charger la liste des fichiers déjà uploadés
  const fetchFiles = async () => {
    try {
      const res = await listFollowUpAttachments(cardId);
      setFiles(res.data || []);
    } catch (e) {
      setFiles([]);
    }
  };

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line
  }, [cardId]);

  // Drag & drop handlers
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  // Gestion de l'upload immédiat (refactorisé)
  const handleFile = async (file: File) => {
    setUploadError(null);
    if (!allowedTypes.includes(file.type)) {
      setUploadError(t('fileTypeNotAllowed'));
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setUploadError(t('fileTooLarge', { max: maxSizeMB }));
      return;
    }
    setUploading(true);
    try {
      await uploadFollowUpAttachment(cardId, file);
      await fetchFiles();
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      setUploadError(t('uploading'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  // Suppression d'un fichier
  const handleDelete = async (filename: string) => {
    setUploading(true);
    try {
      await deleteFollowUpAttachment(cardId, filename);
      await fetchFiles();
    } catch (e) {
      setUploadError('Erreur lors de la suppression du fichier.');
    } finally {
      setUploading(false);
    }
  };

  // Fonction utilitaire pour tronquer le nom du fichier
  const truncateFileName = (name: string, max = 30) => {
    if (name.length <= max) return name;
    const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
    const base = name.replace(ext, '');
    return base.slice(0, max - ext.length - 3) + '...' + ext;
  };

  // Affichage
  const renderFiles = () => (
    <ul className="mt-3 space-y-2">
      {files.map((file: FileAttachment) => (
        <li
          key={file.name}
          className="flex items-center gap-3 rounded bg-gray-50 px-3 py-2 text-xs shadow-sm"
        >
          <FiFile className="text-linko size-4" />
          <a
            href={`/app-web/follow-up-mail/attachment-download/${cardId}/${encodeURIComponent(file.name)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-linko max-w-[120px] truncate font-medium hover:underline"
            download
            title={file.name}
          >
            {truncateFileName(file.name)}
          </a>
          <span className="text-gray-400">
            ({(file.size / 1024 / 1024).toFixed(2)} Mo)
          </span>
          <button
            type="button"
            className="ml-auto rounded p-1 text-danger hover:bg-red-50"
            onClick={() => handleDelete(file.name)}
            disabled={uploading || disabled}
            title={t('delete')}
          >
            <FiTrash2 className="size-4" />
          </button>
        </li>
      ))}
    </ul>
  );

  // Formik ou non
  return (
    <div className={cn('relative flex flex-col', containerStyle)}>
      {label && (
        <label className={cn('text-xs font-medium text-gray-600', labelStyle)}>
          {label}
          {isRequired && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      <div
        className={cn(
          'mt-2 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-linko-light px-4 py-6 transition-colors',
          isDragActive
            ? 'border-linko bg-linko-light/10'
            : 'border-gray-300 bg-gray-50',
          inputStyle,
        )}
        onClick={() => !uploading && !disabled && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ cursor: uploading || disabled ? 'not-allowed' : 'pointer' }}
        tabIndex={0}
        role="button"
        aria-disabled={uploading || disabled}
      >
        <FiUpload
          className={cn(
            'text-linko mb-2',
            isDragActive ? 'animate-bounce' : '',
            'size-7',
          )}
        />
        <span className="text-center text-xs text-gray-600">
          {uploading
            ? t('uploading')
            : isDragActive
              ? t('dropHere')
              : t('clickOrDrop')}
        </span>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          disabled={uploading || disabled}
          accept={allowedTypes.join(',')}
          tabIndex={-1}
        />
      </div>
      {renderFiles()}
      {(uploadError || error) && (
        <span className="mt-1 text-xs text-danger">{uploadError || error}</span>
      )}
      {helper && <InfoBubble message={helper} className="mt-1" />}
      {useFormik && name && (
        <ErrorMessage
          name={name}
          component="span"
          className="text-xs text-danger"
        />
      )}
    </div>
  );
};
